import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import axios from "axios";
import "./SlipUploadPanel.css";
import {
  formatAmount,
  formatDate,
  readQrPayloadFromFile,
  resolveImageUrl,
  statusClassName,
} from "./slipUploadUtils";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "https://api.pgzone88.com"
).replace(/\/+$/, "");

export default function SlipUploadPanel({
  userId,
  bankAccounts,
  onUploadFinished,
}) {
  const [amount, setAmount] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [qrPayload, setQrPayload] = useState("");
  const [decodeError, setDecodeError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitMessage, setSubmitMessage] = useState("");
  const [isDecoding, setIsDecoding] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const hasBanks = bankAccounts.length > 0;

  const canSubmit = useMemo(
    () =>
      Boolean(userId) &&
      Boolean(selectedFile) &&
      Boolean(qrPayload) &&
      Number(amount) > 0 &&
      !isDecoding &&
      !isSubmitting,
    [amount, isDecoding, isSubmitting, qrPayload, selectedFile, userId],
  );

  const loadHistory = useCallback(async () => {
    if (!userId) {
      return;
    }

    setHistoryLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/slip-upload/history/${userId}`,
        {
          params: {
            limit: 20,
          },
        },
      );
      setHistory(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error("Load slip upload history error:", error);
    } finally {
      setHistoryLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0] || null;

    setSelectedFile(file);
    setQrPayload("");
    setDecodeError("");
    setSubmitError("");
    setSubmitMessage("");

    if (!file) {
      return;
    }

    setIsDecoding(true);
    try {
      const payload = await readQrPayloadFromFile(file);
      setQrPayload(payload);
    } catch (error) {
      setDecodeError(error.message || "ไม่สามารถอ่าน QR Code ได้");
    } finally {
      setIsDecoding(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setSubmitMessage("");

    const formData = new FormData();
    formData.append("userId", String(userId));
    formData.append("amount", String(parseInt(amount, 10)));
    formData.append("payload", qrPayload);
    formData.append("slipImage", selectedFile);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/slip-upload/verify-bank`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data?.success) {
        setSubmitMessage(response.data?.message || "อัปโหลดสลิปสำเร็จ");
      } else {
        setSubmitError(
          response.data?.error?.message ||
            "ระบบรับสลิปแล้ว แต่ยังตรวจสอบไม่สำเร็จ",
        );
      }
    } catch (error) {
      setSubmitError(
        error.response?.data?.error?.message ||
          error.message ||
          "ไม่สามารถอัปโหลดสลิปได้",
      );
    } finally {
      await loadHistory();
      if (typeof onUploadFinished === "function") {
        onUploadFinished();
      }
      setIsSubmitting(false);
    }
  };

  return (
    <div className="slip-upload-card">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <div>
          <Icon icon="solar:camera-bold-duotone" width={32} height={32} />
          <div style={{ fontSize: 16, fontWeight: 600 }}>อัปโหลดสลิป</div>
          <div style={{ fontSize: 12, opacity: 0.9 }}>
            ระบบจะอ่าน QR บนสลิปและส่งตรวจสอบอัตโนมัติ
          </div>
        </div>
      </div>

      <div className="slip-upload-grid">
        <div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 14, marginBottom: 8 }}>ยอดเงินที่โอน</div>
            <input
              type="number"
              min="1"
              step="1"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="กรอกจำนวนเงินตามสลิป"
              style={{
                padding: 14,
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.18)",
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
                fontSize: 16,
              }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 14, marginBottom: 8 }}>รูปสลิป</div>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleFileChange}
              style={{
                padding: 14,
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.18)",
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
              }}
            />
          </div>

          {isDecoding ? (
            <div style={{ fontSize: 13, color: "#ccfbf1", marginBottom: 10 }}>
              กำลังอ่าน QR Code จากรูปสลิป...
            </div>
          ) : null}

          {decodeError ? (
            <div
              style={{
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                background: "rgba(248, 113, 113, 0.15)",
                color: "#fecaca",
                fontSize: 13,
              }}
            >
              {decodeError}
            </div>
          ) : null}

          {qrPayload ? (
            <div
              style={{
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                background: "rgba(52, 211, 153, 0.16)",
                color: "#d1fae5",
                fontSize: 12,
                wordBreak: "break-all",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                QR Payload พร้อมส่งตรวจสอบ
              </div>
              <div>{qrPayload}</div>
            </div>
          ) : null}

          {submitError ? (
            <div
              style={{
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                background: "rgba(248, 113, 113, 0.15)",
                color: "#fecaca",
                fontSize: 13,
              }}
            >
              {submitError}
            </div>
          ) : null}

          {submitMessage ? (
            <div
              style={{
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                background: "rgba(52, 211, 153, 0.16)",
                color: "#d1fae5",
                fontSize: 13,
              }}
            >
              {submitMessage}
            </div>
          ) : null}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              width: "100%",
              padding: 12,
              borderRadius: 10,
              border: "none",
              background: canSubmit ? "#f59e0b" : "#64748b",
              color: "#0f172a",
              fontSize: 16,
              fontWeight: 700,
              cursor: canSubmit ? "pointer" : "not-allowed",
              opacity: isSubmitting ? 0.7 : 1,
            }}
          >
            {isSubmitting ? "กำลังอัปโหลดสลิป..." : "ยืนยันอัปโหลดสลิป"}
          </button>
        </div>

        <div className="slip-upload-preview">
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
            พรีวิวสลิป
          </div>
          {previewUrl ? (
            <img src={previewUrl} alt="Slip preview" />
          ) : (
            <div
              style={{
                minHeight: 220,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                color: "rgba(255,255,255,0.72)",
                fontSize: 13,
              }}
            >
              เลือกรูปสลิปเพื่อดูตัวอย่างและอ่าน QR Code
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>
          บัญชีฝากที่รองรับ
        </div>
        {hasBanks ? (
          <div className="slip-upload-bank-list">
            {bankAccounts.map((bank) => (
              <div
                key={`${bank.BanklistID || bank.banknumber}`}
                className="slip-upload-bank-item"
              >
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  {String(bank.bankname || "").toUpperCase()}
                </div>
                <div style={{ fontSize: 13 }}>{bank.bankownername}</div>
                <div style={{ fontSize: 13, opacity: 0.84 }}>
                  {bank.banknumber}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 13, opacity: 0.8 }}>
            ยังไม่พบบัญชีฝากหลักที่เปิดใช้งาน
          </div>
        )}
      </div>

      <div className="slip-upload-history">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 16px 10px",
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            ประวัติการอัปโหลดสลิป
          </div>
          <button
            type="button"
            onClick={loadHistory}
            disabled={historyLoading}
            style={{
              border: "1px solid rgba(255,255,255,0.16)",
              background: "rgba(255,255,255,0.08)",
              color: "#fff",
              borderRadius: 999,
              padding: "8px 12px",
              cursor: historyLoading ? "not-allowed" : "pointer",
            }}
          >
            {historyLoading ? "กำลังโหลด..." : "รีเฟรช"}
          </button>
        </div>

        <div className="slip-upload-history-scroll">
          <table className="slip-upload-history-table">
            <thead>
              <tr>
                <th>ยอดเงิน</th>
                <th>ธนาคารที่ฝาก</th>
                <th>สถานะ</th>
                <th>วัน-เวลา</th>
                <th>รายละเอียด</th>
                <th>สลิป</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", opacity: 0.8 }}>
                    ยังไม่มีประวัติการอัปโหลดสลิป
                  </td>
                </tr>
              ) : (
                history.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div>
                        ฿
                        {formatAmount(
                          item.verifiedAmount || item.requestedAmount,
                        )}
                      </div>
                      {item.requestedAmount ? (
                        <div style={{ opacity: 0.72, fontSize: 12 }}>
                          ระบุ: ฿{formatAmount(item.requestedAmount)}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div>{item.bankName || "-"}</div>
                      <div style={{ opacity: 0.72, fontSize: 12 }}>
                        {item.senderName || "ไม่ทราบชื่อผู้โอน"}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`slip-upload-status ${
                          statusClassName[item.status] || "pending"
                        }`}
                      >
                        {item.statusLabel || item.status}
                      </span>
                    </td>
                    <td>{formatDate(item.createdAt || item.slipDate)}</td>
                    <td>
                      <div>
                        {item.failureMessage ||
                          item.easyslipMessage ||
                          "ตรวจสอบแล้ว"}
                      </div>
                      {item.transRef ? (
                        <div style={{ opacity: 0.72, fontSize: 12 }}>
                          Ref: {item.transRef}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      {item.imageUrl ? (
                        <a
                          href={resolveImageUrl(item.imageUrl)}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "#fcd34d", fontWeight: 700 }}
                        >
                          ดูรูป
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
